'use client';

import { cn, getColorwayWithTransparency } from '@/utils/styles';
import { useRef, useEffect, useCallback } from 'react';
import { CardsVerticalScrollProps } from './types';
import { Colorways } from '@/types/constants';
import { trackExpandableCardItemToggled } from '../analytics/eventHandlerTracking';
import { useExpandableCardsItemToggleTracking } from '../analytics/hooks';
import { ANALYTICS_LABEL } from './constants';
import {
  CardsVerticalScrollItemDesktopContent,
  CardsVerticalScrollItemDesktopMedia,
} from './CardsVerticalScrollDesktopItem';
import { useCardsVerticalScrollDesktop } from './useCardsVerticalScrollDesktop';

const CardsVerticalScrollDesktop = ({ sectionName, transparentBackground, cards }: CardsVerticalScrollProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardsExpandTracking = useExpandableCardsItemToggleTracking({
    label: ANALYTICS_LABEL,
  });

  const cardItems = cards?.items ?? [];
  const lastIndex = cardItems.length - 1;

  const handleActiveCardChange = useCallback(() => {
    cardsExpandTracking();
    trackExpandableCardItemToggled({
      action: 'open',
    });
  }, [cardsExpandTracking]);

  const { containerRef, activeCardIndex, scrollHeight, scrollToCard } = useCardsVerticalScrollDesktop({
    cardCount: cardItems.length,
    onActiveCardChange: handleActiveCardChange,
  });

  const handleCardClick = useCallback(
    (index: number) => {
      const position = index === 0 ? 'start' : index === lastIndex ? 'end' : 'middle';
      scrollToCard(index, position);
    },
    [scrollToCard, lastIndex],
  );

  // Initialize card refs array
  useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, cardItems.length);
  }, [cardItems.length]);

  // Keyboard navigation for Up/Down arrows
  // Position at boundaries enables immediate page scroll on next key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't intercept keyboard navigation in interactive elements (inputs, textareas, etc.)
      const target = event.target as HTMLElement;
      const isInteractiveElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;

      if (isInteractiveElement) return;

      // Only intercept when the sticky container is fully visible in the viewport
      const stickyContainer = scrollContainerRef.current;
      if (!stickyContainer) return;

      const rect = stickyContainer.getBoundingClientRect();
      const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isFullyVisible) return;

      if (event.key === 'ArrowUp' && activeCardIndex > 0) {
        event.preventDefault();
        const targetIndex = activeCardIndex - 1;
        // Position at 'start' when navigating to first card so next ArrowUp scrolls page
        const position = targetIndex === 0 ? 'start' : 'middle';
        scrollToCard(targetIndex, position);
      } else if (event.key === 'ArrowDown' && activeCardIndex < lastIndex) {
        event.preventDefault();
        const targetIndex = activeCardIndex + 1;
        // Position at 'end' when navigating to last card so next ArrowDown scrolls page
        const position = targetIndex === lastIndex ? 'end' : 'middle';
        scrollToCard(targetIndex, position);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeCardIndex, scrollToCard, lastIndex]);

  const internalClassNames = {
    wrapper: cn('relative', getColorwayWithTransparency(Colorways.DarkGreenGradientLeftToRight, transparentBackground)),
    stickyContainer: cn('sticky top-0 h-screen flex items-center justify-center', 'px-14 py-20'),
    contentWrapper: cn('w-full mx-auto content-wrapper py-14 rounded-3xl overflow-hidden'),
    innerContent: cn('flex items-start justify-between gap-4'),
    leftColumn: cn('font-sans grid gap-4 w-[492px]'),
    rightColumn: cn('relative h-[400px] w-[400px] aspect-square', 'ml:w-[596px] ml:h-[596px]'),
  };

  return (
    <div ref={containerRef} id={sectionName} className={internalClassNames.wrapper} data-testid="desktop-version">
      <div style={{ height: scrollHeight }}>
        <div ref={scrollContainerRef} className={internalClassNames.stickyContainer}>
          <div
            className={internalClassNames.contentWrapper}
            style={{
              backgroundColor: '#0A3E26',
              backgroundImage: 'linear-gradient(145deg, #0A3E26 10%, #052013 35%)',
            }}>
            <div className={internalClassNames.innerContent}>
              {/* Left Column - Accordion Items */}
              <div className={internalClassNames.leftColumn}>
                {cardItems.map((card, index) => (
                  <CardsVerticalScrollItemDesktopContent
                    key={card.sys.id}
                    card={card}
                    index={index}
                    isActive={activeCardIndex === index}
                    cardRef={el => {
                      cardRefs.current[index] = el;
                    }}
                    bulletStyle={card.bulletStyle}
                    onClick={() => handleCardClick(index)}
                  />
                ))}
              </div>

              {/* Right Column - Media Display */}
              <div className={internalClassNames.rightColumn}>
                {cardItems.map((card, index) => (
                  <CardsVerticalScrollItemDesktopMedia
                    key={`${card.sys.id}-media`}
                    card={card}
                    isActive={activeCardIndex === index}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardsVerticalScrollDesktop;
